/**
 * Main sidebar nav composition, lifted out of SidebarMenu.js so both the
 * old sidebar (via the `legacy` shape) and the new one (via the `nav`
 * shape, matching AppShell/Sidebar.jsx's expected
 * {top,widgets,reports,standalone,platformSettings} prop) share one source
 * of truth for "which nav items show for this user" while both shells
 * coexist behind the app-shell flag. Eligibility logic (appConfig.widgets
 * filter, adminOnly/hidden gates, the admins+showAdminNav gate on Platform
 * settings) is unchanged from SidebarMenu.js's prior inline version.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../../../hooks/redux/user";
import { useAutologinStore } from "../../../../hooks/redux/autologin";
import { useAppStore } from "../../../../hooks/redux/app";
import appConfig from "../../../../appConfig";
import {
  widgetMenuOptions,
  userMenuOptions,
  reportsMenuOptions,
  getAdminOptions,
} from "./sidebarNavOptions";

const toNavChild = (item) => ({ key: item.id, label: item.label, to: item.url });
const toNavItem = (item) => ({ key: item.id, label: item.label, icon: item.icon, to: item.url });

export function useSidebarNavData() {
  const { user } = useUserStore();
  const { autologin } = useAutologinStore();
  const { app } = useAppStore();
  const { t } = useTranslation();

  const userRole = useMemo(() => (user.model ? user.model.role : ""), [user]);
  const adminOptionsDisable = autologin.loginSuccess;
  const currentYear = new Date().getFullYear();

  const visibleUserMenuOptions = useMemo(
    () =>
      userMenuOptions.filter((item) => {
        if (item.adminOnly && userRole !== "admins") return false;
        if (
          item.hidden &&
          adminOptionsDisable &&
          window.location.hostname !== `dev.${appConfig.domain}`
        ) {
          return false;
        }
        return true;
      }),
    [userRole, adminOptionsDisable]
  );

  const visibleReportsMenuOptions = useMemo(
    () => reportsMenuOptions.filter((item) => !item.adminOnly || userRole === "admins"),
    [userRole]
  );

  const adminOptions = useMemo(
    () => getAdminOptions(t, currentYear, adminOptionsDisable),
    [t, currentYear, adminOptionsDisable]
  );

  const showPlatformSettings = userRole === "admins" && app.showAdminNav;

  const legacy = {
    widgetMenuOptions,
    reportsMenuOptions: visibleReportsMenuOptions,
    userMenuOptions: visibleUserMenuOptions,
    adminOptions,
    showPlatformSettings,
  };

  const nav = useMemo(
    () => ({
      top: [{ key: "dashboard", label: "Dashboard", icon: "HomeLine", to: "/dashboard" }],
      widgets: widgetMenuOptions.length
        ? { label: "Widgets", icon: "Grid01", children: widgetMenuOptions.map(toNavChild) }
        : undefined,
      reports: visibleReportsMenuOptions.length
        ? { label: "Reports", icon: "BarChart08", children: visibleReportsMenuOptions.map(toNavChild) }
        : undefined,
      standalone: visibleUserMenuOptions.map(toNavItem),
      platformSettings: showPlatformSettings
        ? { label: "Platform settings", icon: "Settings01", children: adminOptions.map(toNavChild) }
        : undefined,
    }),
    [visibleReportsMenuOptions, visibleUserMenuOptions, showPlatformSettings, adminOptions]
  );

  return { legacy, nav };
}
